import { Fragment, useEffect, useMemo } from "react";
import { TopShopNotAppliedCard } from "@/components/topshop/TopShopNotAppliedCard";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, ChevronDown, GraduationCap, Loader2, Lock } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CohortDiscussionList } from "@/components/discussions/CohortDiscussionList";
import { useProfile } from "@/hooks/useProfile";
import { cn } from "@/lib/utils";
import illustration from "@/assets/topshop/topshop-illustration.webp";
import { TopShopStatusBadge, useMyTopShopApplication } from "@/components/topshop/topShopShared";
import {
  ChallengeItem, ChallengeState, useTopShopChallenges, useTopShopLeaderboard, useTopShopProgram,
} from "@/components/topshop/useTopShopChallenge";
import { TopShopApplicationPanel } from "./TopShopApplication";
import { evidenceCountLabel, useEvidenceCounts } from "@/components/topshop/TopShopChallengeEvidence";

const TABS = ["overview", "challenges", "scores", "leaderboard", "learning", "community", "application"] as const;

function fmt(n: number) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function statusCopy(status: string, firstName: string, photoCount: number) {
  if (status === "accepted") return { message: `Congratulations ${firstName}, you're in the Top Shop Challenge.`, label: "View application", href: "/topshop/hub?tab=application", tone: "bg-success text-success-foreground" };
  if (status === "completed") return { message: `Congratulations ${firstName}, you completed the Top Shop Challenge. Keep learning and growing on EAB Connect.`, label: "Go to EAB Connect", href: "/dashboard", tone: "bg-success text-success-foreground" };
  if (status === "withdrawn") return { message: `Thank you for taking part, ${firstName}. You still have full access to EAB Connect training and mentors.`, label: "Go to EAB Connect", href: "/dashboard", tone: "bg-muted text-foreground" };
  if (status === "waitlisted") return { message: `You're on the waitlist, ${firstName}. We'll let you know if a place opens up.`, label: "View application", href: "/topshop/hub?tab=application", tone: "bg-warning text-foreground" };
  if (status === "declined") return { message: `Thank you for applying, ${firstName}. You still have full access to EAB Connect training and mentors.`, label: "Go to EAB Connect", href: "/dashboard", tone: "bg-muted text-foreground" };
  if (photoCount === 0) return { message: `Your application is in, ${firstName}. Add photos of your shop to complete it.`, label: "Add photos", href: "/topshop/hub?tab=application", tone: "bg-warning text-foreground" };
  return { message: `Your application is complete and under review, ${firstName}.`, label: "View application", href: "/topshop/hub?tab=application", tone: "bg-primary text-primary-foreground" };
}

function LockedPanel({ declined }: { declined: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 p-10 text-center">
        <Lock className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        {declined ? (
          <>
            <p className="max-w-md">Thank you for applying. You still have full access to EAB Connect training and mentors.</p>
            <Button asChild><Link to="/dashboard">Go to Dashboard</Link></Button>
          </>
        ) : (
          <p className="max-w-md">This opens when you are accepted into the challenge.</p>
        )}
      </CardContent>
    </Card>
  );
}

const STATE_PILL: Record<ChallengeState, { label: string; className: string }> = {
  locked: { label: "Opens soon", className: "bg-muted text-muted-foreground" },
  not_started: { label: "Not started", className: "bg-warning/15 text-foreground border border-warning" },
  submitted: { label: "Submitted", className: "bg-primary text-primary-foreground" },
  reviewed: { label: "Reviewed", className: "bg-success text-success-foreground" },
};

function ChallengeCard({ c, courseId, counts }: { c: ChallengeItem; courseId: string | null; counts?: { photos: number; links: number } }) {
  const pill = STATE_PILL[c.state];
  const done = c.state === "submitted" || c.state === "reviewed";
  const href = courseId && c.moduleId
    ? `/courses/${courseId}?module=${c.moduleId}${c.assignmentId ? `&assignment=${c.assignmentId}` : ""}${done ? "&action=view" : ""}`
    : null;
  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="text-2xl font-bold text-primary tabular-nums">{c.number}</span>
            <div>
              <CardTitle className="text-lg">{c.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{c.focusAreaName}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className={cn("rounded-md px-2.5 py-0.5 text-xs font-semibold", pill.className)}>{pill.label}</span>
            {c.state !== "locked" && <span className="text-xs text-muted-foreground">{evidenceCountLabel(counts)}</span>}
          </div>
        </div>
        {c.description && <CardDescription>{c.description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {c.brief && (
          <Collapsible>
            <CollapsibleTrigger className="group flex items-center gap-1 text-sm font-medium text-primary">
              Challenge brief <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-3 text-sm leading-6">
              {c.brief.split(/\n\s*\n/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
            </CollapsibleContent>
          </Collapsible>
        )}
        {c.criteria.length > 0 && (
          <div>
            <p className="text-sm font-medium">How it is scored</p>
            <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
              {c.criteria.map((cr) => (
                <li key={cr.id} className="flex justify-between gap-3"><span>{cr.name}</span><span className="tabular-nums">{cr.min_points} to {cr.max_points} points</span></li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {c.areaScore !== null ? (
            <p className="text-sm font-medium">Score: {fmt(c.areaScore)} of {c.areaMax}</p>
          ) : <span />}
          {c.state === "locked" || !href ? (
            <Button disabled>Open challenge</Button>
          ) : (
            <Button asChild><Link to={href}>{done ? "View submission" : "Open challenge"}</Link></Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function TopShopHub() {
  const [params, setParams] = useSearchParams();
  const { data: profile } = useProfile();
  const { data: appData, isLoading } = useMyTopShopApplication();
  const { data: program } = useTopShopProgram();
  const status: string | undefined = appData?.application?.status;
  const accepted = status === "accepted" || status === "completed";
  const declined = status === "declined" || status === "withdrawn";

  const { data: challenges, isLoading: chLoading } = useTopShopChallenges(program, appData?.application?.id, accepted);
  const { data: evidenceCounts } = useEvidenceCounts(accepted ? appData?.application?.id : null);
  const lbQuery = useTopShopLeaderboard(accepted && !!program?.leaderboard_visible);
  const showLeaderboard = accepted && !!program?.leaderboard_visible && !lbQuery.isError;

  useEffect(() => { document.title = "Top Shop Challenge | EAB Connect"; }, []);

  const raw = params.get("tab") ?? "overview";
  const tab = (TABS as readonly string[]).includes(raw) && (raw !== "leaderboard" || showLeaderboard) ? raw : "overview";
  const setTab = (v: string) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", v); return n; }, { replace: true });

  const fullName = (profile as any)?.full_name ?? "";
  const firstName = fullName.split(" ")[0] || "there";

  const totals = useMemo(() => {
    const list = challenges ?? [];
    return {
      submitted: list.filter((c) => c.state === "submitted" || c.state === "reviewed").length,
      reviewed: list.filter((c) => c.state === "reviewed").length,
      max: list.reduce((s, c) => s + c.areaMax, 0),
      score: list.reduce((s, c) => s + (c.areaScore ?? 0), 0),
      count: list.length,
    };
  }, [challenges]);

  const myCommunity: string | undefined = appData?.application?.community?.name;

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center gap-3">
          <img src={illustration} alt="" className="h-12 w-12 object-contain" />
          <h1 className="text-2xl font-bold">Top Shop Challenge</h1>
          {status && <TopShopStatusBadge status={status} />}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : !appData ? (
          <TopShopNotAppliedCard />
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <TabsList className="inline-flex w-max">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="challenges">Challenges</TabsTrigger>
                <TabsTrigger value="scores">My scores</TabsTrigger>
                {showLeaderboard && <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>}
                <TabsTrigger value="learning">Learning</TabsTrigger>
                <TabsTrigger value="community">Community</TabsTrigger>
                <TabsTrigger value="application">My application</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-4">
              {(() => {
                const s = statusCopy(status!, firstName, appData.photos.length);
                return (
                  <div className={cn("flex flex-col gap-3 rounded-md p-4 md:flex-row md:items-center md:justify-between", s.tone)}>
                    <p className="font-medium">{s.message}</p>
                    <Button asChild variant="outline" className="border-background bg-background text-foreground hover:bg-background/90">
                      <Link to={s.href} onClick={() => s.href.includes("tab=application") && setTab("application")}>{s.label}</Link>
                    </Button>
                  </div>
                );
              })()}
              {accepted && (
                <div className="grid gap-4 sm:grid-cols-3">
                  <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Challenges submitted</p><p className="text-2xl font-bold">{chLoading ? "..." : `${totals.submitted} of 7`}</p></CardContent></Card>
                  <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Challenges reviewed</p><p className="text-2xl font-bold">{chLoading ? "..." : `${totals.reviewed} of 7`}</p></CardContent></Card>
                  {program?.scores_visible && (
                    <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total score</p><p className="text-2xl font-bold">{chLoading ? "..." : `${fmt(totals.score)} of ${totals.max}`}</p></CardContent></Card>
                  )}
                </div>
              )}
              <Card>
                <CardHeader><CardTitle className="text-lg">Key dates</CardTitle></CardHeader>
                <CardContent>
                  <ul className="list-disc space-y-1 pl-5 text-sm">
                    <li>Challenge runs November 2026 to April 2027</li>
                    <li>Jamaica Microbusiness Development Summit, Montego Bay Conference Centre, April 2027</li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="challenges" className="space-y-4">
              {!accepted ? <LockedPanel declined={declined} /> : chLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : (challenges ?? []).map((c) => <ChallengeCard key={c.focusAreaId} c={c} courseId={program?.course_id ?? null} counts={evidenceCounts?.[c.focusAreaId]} />)}
            </TabsContent>

            <TabsContent value="scores">
              {!accepted ? <LockedPanel declined={declined} /> : !program?.scores_visible ? (
                <Card><CardContent className="p-6">Scores will appear here once judging is complete.</CardContent></Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader><TableRow><TableHead>Challenge</TableHead><TableHead className="text-right">Points</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(challenges ?? []).map((c) => (
                          <Fragment key={c.focusAreaId}>
                            <TableRow className="bg-muted/40">
                              <TableCell className="font-semibold">{c.number}. {c.focusAreaName}</TableCell>
                              <TableCell className="text-right font-semibold tabular-nums">{fmt(c.areaScore ?? 0)} / {c.areaMax}</TableCell>
                            </TableRow>
                            {c.criteria.map((cr) => (
                              <TableRow key={cr.id}>
                                <TableCell className="pl-8">{cr.name}</TableCell>
                                <TableCell className="text-right tabular-nums">{cr.id in c.pointsByCriterion ? fmt(c.pointsByCriterion[cr.id]) : "Not scored"} / {cr.max_points}</TableCell>
                              </TableRow>
                            ))}
                          </Fragment>
                        ))}
                        <TableRow>
                          <TableCell className="font-bold">Total</TableCell>
                          <TableCell className="text-right font-bold tabular-nums">{fmt(totals.score)} / {totals.max}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {showLeaderboard && (
              <TabsContent value="leaderboard" className="space-y-4">
                {lbQuery.isLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                ) : (
                  <>
                    <Card>
                      <CardHeader><CardTitle className="text-lg">Community standings</CardTitle></CardHeader>
                      <CardContent className="overflow-x-auto p-0">
                        <Table>
                          <TableHeader><TableRow><TableHead>Rank</TableHead><TableHead>Community</TableHead><TableHead>Parish</TableHead><TableHead className="text-right">Participants</TableHead><TableHead className="text-right">Average score</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {(lbQuery.data?.communities ?? []).map((r) => (
                              <TableRow key={r.community} className={cn(r.community === myCommunity && "bg-primary/10 font-semibold")}>
                                <TableCell>{r.rank}</TableCell><TableCell>{r.community}</TableCell><TableCell>{r.parish ?? ""}</TableCell>
                                <TableCell className="text-right">{r.participants}</TableCell><TableCell className="text-right tabular-nums">{Number(r.average_score).toFixed(1)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                    {lbQuery.data?.individuals && (
                      <Card>
                        <CardHeader><CardTitle className="text-lg">Top shops</CardTitle></CardHeader>
                        <CardContent className="overflow-x-auto p-0">
                          <Table>
                            <TableHeader><TableRow><TableHead>Rank</TableHead><TableHead>Name</TableHead><TableHead>Shop</TableHead><TableHead>Community</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                            <TableBody>
                              {lbQuery.data.individuals.map((r, i) => (
                                <TableRow key={i}>
                                  <TableCell>{r.rank}</TableCell><TableCell>{r.full_name ?? ""}</TableCell><TableCell>{r.business_name}</TableCell>
                                  <TableCell>{r.community ?? ""}</TableCell><TableCell className="text-right tabular-nums">{fmt(Number(r.total_score))}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </TabsContent>
            )}

            <TabsContent value="learning" className="grid gap-4 sm:grid-cols-2">
              {program?.course_id && (
                <Card>
                  <CardHeader><BookOpen className="h-6 w-6 text-primary" /><CardTitle className="text-lg">Top Shop Challenges</CardTitle><CardDescription>The course where you complete and submit each challenge.</CardDescription></CardHeader>
                  <CardContent><Button asChild><Link to={`/courses/${program.course_id}`}>Open course</Link></Button></CardContent>
                </Card>
              )}
              <Card>
                <CardHeader><GraduationCap className="h-6 w-6 text-primary" /><CardTitle className="text-lg">Explore all EAB Connect learning</CardTitle><CardDescription>Training, courses and tools open to every member.</CardDescription></CardHeader>
                <CardContent><Button asChild variant="outline"><Link to="/curricula">Browse learning</Link></Button></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="community">
              {!accepted ? <LockedPanel declined={declined} /> : program?.cohort_id ? (
                <CohortDiscussionList cohortId={program.cohort_id} />
              ) : (
                <Card><CardContent className="p-6">The community space is not set up yet.</CardContent></Card>
              )}
            </TabsContent>

            <TabsContent value="application">
              <TopShopApplicationPanel />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
